import { faroCoreApiClient } from "@api/faro-core-api-client";
import { useEffect, useState } from "react";
import { useAppNavigation } from "@hooks/navigation/use-app-navigation";
import { useToast } from "@flat-ui";
import { useCompanyId } from "@context-providers/company-id-provider";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { SubscriptionExpiredEvents } from "@utils/track-event/track-event-list";
/** Helper function to manage subscription expired */
export function useSubscriptionExpiredPage({
  canViewProductDetails,
  subscriptionStatus
}) {
  const {
    navigateToProjects
  } = useAppNavigation();
  const {
    openToast
  } = useToast();
  const {
    handleErrorWithToast
  } = useErrorContext();
  const companyId = useCompanyId();
  const {
    trackEvent
  } = useTrackEvent();
  const [canApplyRetentionOffer, setCanApplyRetentionOffer] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [retentionOfferDuration, setRetentionOfferDuration] = useState(null);

  /** Fetch active company products and update subscription condition */
  useEffect(() => {
    async function getActiveCompanyProducts(companyId_0) {
      setIsLoading(true);
      try {
        const activeCompanyProducts = await faroCoreApiClient.company.getActiveCompanyProducts({
          companyId: companyId_0
        });
        const companyRetentionOffer = activeCompanyProducts?.find(product => product.identifier === "retention-offer");
        setCanApplyRetentionOffer(!!companyRetentionOffer?.availability.isAvailable);
        const companyRetentionOfferDuration = companyRetentionOffer?.expirationDays ?? null;

        /** Retention offer duration should not be null */
        if (companyRetentionOfferDuration === null) {
          throw new Error("Retention offer duration is missing");
        }
        setRetentionOfferDuration(companyRetentionOfferDuration);
      } catch (error) {
        // Handle error directly here in order to show specific info about the field that failed: thumbnailUrl
        // Otherwise the error will be handled by the error slice, but that will show a generic message without details
        handleErrorWithToast({
          id: `SubscriptionExpired-getActiveCompanyProducts-${Date.now().toString()}`,
          title: "Could not fetch company products",
          error
        });
      } finally {
        setIsLoading(false);
      }
    }
    void getActiveCompanyProducts(companyId);
  }, [companyId, handleErrorWithToast]);
  function getSubtitle() {
    if (subscriptionStatus === "subscription-ended") {
      if (canApplyRetentionOffer) {
        return canViewProductDetails ? "Your subscription has ended. To keep your workspace active, we're offering a 14-day extension with Enterprise features. Renew or upgrade anytime during this period." : "The subscription has ended. Please contact your admin to restore access.";
      } else {
        return canViewProductDetails && retentionOfferDuration ? `Your ${retentionOfferDuration}-day trial has ended. Renew or upgrade your plan to continue working on your projects, or contact us for more information` : "This trial has ended. Please contact your admin to restore access.";
      }
    } else if (subscriptionStatus === "trial-ended") {
      return canViewProductDetails ? "Your trial has ended. Upgrade your plan to continue working on your project, or contact us for more information." : "This trial has ended. Please contact your admin to restore access.";
    }
    return "";
  }

  /** Callback when user requests 14 day retention offer */
  async function onRetentionRequested() {
    trackEvent({
      name: SubscriptionExpiredEvents.requestRetentionOffer
    });
    try {
      await faroCoreApiClient.company.updateActiveProducts({
        companyId,
        payload: {
          identifier: "retention-offer"
        }
      });
      openToast({
        title: `${retentionOfferDuration}-day trial is added to your account`
      });
      navigateToProjects({
        companyId
      });
    } catch (error_0) {
      handleErrorWithToast({
        id: `SubscriptionExpired-updateActiveProducts-${Date.now().toString()}`,
        title: "Failed to apply retention offer. Please try again or reload the page",
        error: error_0
      });
    }
  }
  return {
    canApplyRetentionOffer,
    subtitle: getSubtitle(),
    retentionOfferDuration,
    onRetentionRequested,
    isLoading
  };
}